// src/components/kiosk/POSSection/CategoryGrid.jsx
import { Grid3x3, Sparkles, Package, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';

function CategoryGrid({ categories, selectedSection, onSelectCategory, onBack, items, onAddToCart }) {
  // Section color mapping
  const sectionColors = {
    'Salon': { bg: '#10b981', light: '#34d399', dark: '#059669', textDark: '#065f46', shadow: 'rgba(16, 185, 129, 0.3)', bgLight: 'rgba(16, 185, 129, 0.15)', bgCard: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
    'Spa': { bg: '#a855f7', light: '#c084fc', dark: '#7c3aed', textDark: '#6b21a8', shadow: 'rgba(168, 85, 247, 0.3)', bgLight: 'rgba(168, 85, 247, 0.15)', bgCard: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' },
    'Gym': { bg: '#3b82f6', light: '#60a5fa', dark: '#2563eb', textDark: '#1e40af', shadow: 'rgba(59, 130, 246, 0.3)', bgLight: 'rgba(59, 130, 246, 0.15)', bgCard: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }
  };

  const colors = sectionColors[selectedSection] || sectionColors['Gym'];

  // Filter categories for selected section
  const sectionCategories = categories.filter(cat => cat.subBusiness === selectedSection);

  // Filter items WITHOUT category for this section
  const categoryLessItems = items.filter(item => {
    const hasNoCategory = !item.category || item.category === '' || item.category === null;
    const matchesSection = item.subBusiness === selectedSection;
    return hasNoCategory && matchesSection;
  });

  const hasCategories = sectionCategories.length > 0;
  const hasCategoryLessItems = categoryLessItems.length > 0;

  if (!hasCategories && !hasCategoryLessItems) {
    return (
      <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 
            style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '0.5rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            {selectedSection}
          </h2>
          <div 
            style={{
              width: '8rem',
              height: '4px',
              margin: '0 auto 2rem',
              borderRadius: '9999px',
              background: `linear-gradient(90deg, ${colors.bg}, ${colors.light})`
            }}
          ></div>
        </div>

        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <Grid3x3 size={64} style={{ color: 'rgba(255, 255, 255, 0.3)', margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', margin: 0 }}>
            No categories or items found for {selectedSection}
          </p>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem' }}>
            Please add categories or items in the admin panel
          </p>
          
          <button
            onClick={onBack}
            style={{
              marginTop: '2rem',
              padding: '0.75rem 2rem',
              fontSize: '14px',
              fontWeight: '600',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.75rem',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
          >
            ← Back to Sections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Title */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 
          style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '0.5rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          {selectedSection}
        </h2>
        <div 
          style={{
            width: '8rem',
            height: '4px',
            margin: '0 auto',
            borderRadius: '9999px',
            background: `linear-gradient(90deg, ${colors.bg}, ${colors.light})`
          }}
        ></div>
      </div>

      {/* Back Button */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={onBack}
          style={{
            padding: '0.5rem 1.5rem',
            fontSize: '13px',
            fontWeight: '600',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '0.5rem',
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
        >
          ← Back to Sections
        </button>
      </div>

      {/* Categories Section */}
      {hasCategories && (
        <div style={{ marginBottom: '2.5rem' }}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {sectionCategories.map((category) => (
              <button
                key={category._id}
                onClick={() => onSelectCategory(category)}
                className="text-base md:text-2xl"
                style={{
                  padding: '1.25rem 1rem',
                  border: 'none',
                  borderRadius: '1rem',
                  background: `linear-gradient(135deg, ${colors.bg} 0%, ${colors.dark} 100%)`,
                  color: '#ffffff',
                  fontWeight: '700',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                  transition: 'all 0.3s ease',
                  fontFamily: 'Inter, sans-serif',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.3)';
                }}
              >
                {/* Decorative background */}
                <div
                  style={{
                    position: 'absolute',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    top: '-50px',
                    right: '-50px',
                    filter: 'blur(60px)'
                  }}
                />
                
                {/* Icon */}
                <div style={{ marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
                  <Sparkles size={28} style={{ margin: '0 auto' }} />
                </div>
                
                {/* Category name */}
                <span style={{ position: 'relative', zIndex: 1, display: 'block' }}>
                  {category.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category-less Items Section */}
      {hasCategoryLessItems && (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))',
              gap: '0.65rem'
            }}
          >
            {categoryLessItems.map(item => (
              <ItemCard 
                key={item._id} 
                item={item} 
                onAddToCart={onAddToCart} 
                sectionColors={colors} 
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemCard({ item, onAddToCart, sectionColors }) {
  const isLowStock = item.type === 'product' && item.quantity < 10;
  const isOutOfStock = item.type === 'product' && item.quantity === 0;

  return (
    <button
      onClick={() => !isOutOfStock && onAddToCart(item)}
      disabled={isOutOfStock}
      style={{
        padding: '0',
        border: `2px solid ${sectionColors.bg}`,
        borderRadius: '0.75rem',
        background: '#ffffff',
        textAlign: 'left',
        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
        opacity: isOutOfStock ? 0.6 : 1,
        transition: 'all 0.3s ease',
        fontFamily: 'Inter, system-ui, sans-serif',
        boxShadow: `0 2px 4px ${sectionColors.shadow}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onMouseEnter={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
          e.currentTarget.style.boxShadow = `0 8px 16px ${sectionColors.shadow}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isOutOfStock) {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = `0 2px 4px ${sectionColors.shadow}`;
        }
      }}
    >
      {/* Colored Header */}
      <div 
        style={{
          padding: '0.6rem',
          background: sectionColors.bgCard,
          color: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '70px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '65px',
            height: '65px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '-15px',
            right: '-15px',
            filter: 'blur(12px)'
          }}
        />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Item Name */}
          <h3
            style={{
              fontSize: '0.7rem',
              fontWeight: '700',
              color: '#ffffff',
              margin: '0 0 0.35rem 0',
              lineHeight: '1.2',
              minHeight: '1.8rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.name}
          </h3>

          {/* Type Badge */}
          <span
            style={{
              display: 'inline-block',
              padding: '0.1rem 0.4rem',
              fontSize: '0.6rem',
              fontWeight: '600',
              borderRadius: '9999px',
              background: 'rgba(255, 255, 255, 0.25)',
              color: '#ffffff'
            }}
          >
            {item.type === 'product' ? 'Product' : 'Service'}
          </span>

          {isLowStock && !isOutOfStock && (
            <AlertCircle size={12} style={{ color: '#fbbf24', position: 'absolute', top: '0.6rem', right: '0.6rem' }} />
          )}
        </div>
      </div>

      {/* White Content Area */}
      <div style={{ padding: '0.6rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Details */}
        <div style={{ fontSize: '0.6rem', color: '#6b7280', marginBottom: '0.4rem', flex: 1 }}>
          {item.type === 'product' && (
            <p style={{ margin: 0, color: isLowStock ? '#f59e0b' : '#6b7280', fontWeight: isLowStock ? '600' : '400' }}>
              📦 {item.quantity} {item.unit || 'units'}
            </p>
          )}
        </div>

        {/* Price */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '0.4rem',
            borderTop: `1px solid ${sectionColors.bgLight}`,
            marginTop: 'auto'
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '0.55rem', color: '#6b7280', margin: '0 0 0.1rem 0' }}>Price</p>
            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: sectionColors.textDark, lineHeight: 1 }}>
              {formatCurrency(item.price)}
            </span>
          </div>
          {isOutOfStock && (
            <span style={{ fontSize: '0.6rem', fontWeight: '600', color: '#dc2626' }}>
              Out
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default CategoryGrid;